import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";

import { useThemeMode, THEME_MODES } from "./ThemeModeContext";
import {
  SETTING_KEYS,
  useSaveGeneralSetting,
} from "../hooks/useGeneralSetting";
import { findSettingByType } from "../helpers/helpers";

/**
 * Carries the theme choice across devices via general-setting/set-setting.
 *
 * localStorage stays the boot source so the pre-paint script can apply a theme
 * before React mounts; it is reconciled with the account once the user loads.
 * Saves are silent — the toggle is its own feedback.
 */
export const useThemeSync = () => {
  const user = useSelector((state) => state.user);
  const { mode, setMode } = useThemeMode();
  const { save } = useSaveGeneralSetting();

  // save's identity churns whenever the user object is replaced; a ref keeps
  // the push effect from re-firing on unrelated user updates.
  const saveRef = useRef(save);
  saveRef.current = save;

  const serverMode = useRef(null); // what we believe the account holds
  const adopted = useRef(false);
  // Set while a server value is being applied. The push effect runs in the same
  // commit as the adopt and would still see the previous mode, so it has to sit
  // out until the adopted value actually lands.
  const adoptingTo = useRef(null);

  useEffect(() => {
    if (!user?.token) {
      adopted.current = false;
      serverMode.current = null;
      adoptingTo.current = null;
      return;
    }
    if (adopted.current) return;
    adopted.current = true;

    const stored = findSettingByType(
      user?.general_settings,
      SETTING_KEYS.systemTheme,
    )?.value;

    if (stored && THEME_MODES.includes(stored)) {
      serverMode.current = stored;
      if (stored !== mode) {
        adoptingTo.current = stored;
        setMode(stored);
      }
      return;
    }

    // Nothing stored yet — seed the account from this device's preference so
    // the next device inherits it.
    serverMode.current = mode;
    saveRef.current(SETTING_KEYS.systemTheme, mode, { notify: false });
  }, [user, mode, setMode]);

  useEffect(() => {
    if (adoptingTo.current !== null) {
      if (mode === adoptingTo.current) adoptingTo.current = null;
      return;
    }
    if (!user?.token || !adopted.current) return;
    if (serverMode.current === mode) return;

    serverMode.current = mode;
    saveRef.current(SETTING_KEYS.systemTheme, mode, { notify: false });
  }, [mode, user?.token]);
};
